import api from './backendClient';

/** IPFS retrieval can exceed default API timeout (30s); large or slow pins need a longer limit. */
const IPFS_DOWNLOAD_TIMEOUT_MS = 180000;

/**
 * Prepare/submit flows hit Besu JSON-RPC (nonce, gas, estimateGas, etc.). Default axios 30s often aborts first.
 */
const BLOCKCHAIN_RPC_API_TIMEOUT_MS = 120000;

/** Public UI config from appsettings (block explorer base URL, etc.). */
export const configApi = {
  getClient: () => api.get('/api/config/client')
};

/** Admin fuel injection: batch native ETH to tokenizer wallets (offline sign) + history. */
export const fuelInjectionApi = {
  prepare: (payload) =>
    api.post('/api/admin/fuel-injection/prepare', payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  submit: (payload) =>
    api.post('/api/admin/fuel-injection/submit', payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  history: () => api.get('/api/admin/fuel-injection/history')
};

export const authApi = {
  login: (payload) => api.post('/api/auth/login', payload),
  refreshToken: (payload) => api.post('/api/auth/refresh-token', payload),
  getProfile: () => api.get('/api/auth/profile'),
  /** Admin only — list platform users. */
  listUsers: () => api.get('/api/auth/users'),
  /** Admin only — creates a user (starts as pending approval until approved). */
  registerUser: (payload) => api.post('/api/auth/users', payload),
  /** Admin only — update profile fields and/or role. */
  updateUser: (userId, payload) => api.put(`/api/auth/users/${userId}`, payload),
  approveUser: (userId) => api.post(`/api/auth/users/${userId}/approve`),
  suspendUser: (userId) => api.post(`/api/auth/users/${userId}/suspend`),
  /** Admin only — set or reset a user's password (e.g. temporary password). */
  setUserPassword: (userId, payload) => api.post(`/api/auth/users/${userId}/set-password`, payload),
  /** Signed-in user — change password (requires current password). */
  changePassword: (payload) => api.post('/api/auth/change-password', payload),
  /** Admin only — save this user’s public EVM address (offline signer / gas payer reference). */
  updateMyWalletAddress: (payload) => api.put('/api/auth/profile/wallet', payload)
};

export const tokenizersApi = {
  list: () => api.get('/api/authorized-tokenizers'),
  getById: (id) => api.get(`/api/authorized-tokenizers/${id}`),
  create: (payload) => api.post('/api/authorized-tokenizers', payload),
  update: (id, payload) => api.put(`/api/authorized-tokenizers/${id}`, payload),
  approve: (id) => api.post(`/api/authorized-tokenizers/${id}/approve`),
  suspend: (id) => api.post(`/api/authorized-tokenizers/${id}/suspend`),
  /** Admin — users assigned as operators for this tokenizer. */
  assignedUsers: (id) => api.get(`/api/authorized-tokenizers/${id}/assigned-users`)
};

export const legalReviewFirmsApi = {
  list: () => api.get('/api/legal-review-firms'),
  getById: (id) => api.get(`/api/legal-review-firms/${id}`),
  create: (payload) => api.post('/api/legal-review-firms', payload),
  update: (id, payload) => api.put(`/api/legal-review-firms/${id}`, payload),
  approve: (id) => api.post(`/api/legal-review-firms/${id}/approve`),
  suspend: (id) => api.post(`/api/legal-review-firms/${id}/suspend`),
  assignedUsers: (id) => api.get(`/api/legal-review-firms/${id}/assigned-users`)
};

export const assetClassTemplatesApi = {
  list: () => api.get('/api/asset-class-templates'),
  listAllAdmin: () => api.get('/api/asset-class-templates/admin/all'),
  getById: (id) => api.get(`/api/asset-class-templates/${id}`),
  create: (payload) => api.post('/api/asset-class-templates', payload),
  update: (id, payload) => api.put(`/api/asset-class-templates/${id}`, payload),
  clone: (id, payload) => api.post(`/api/asset-class-templates/${id}/clone`, payload),
  deactivate: (id) => api.post(`/api/asset-class-templates/${id}/deactivate`)
};

export const assetClassesApi = {
  list: () => api.get('/api/asset-classes'),
  getById: (id) => api.get(`/api/asset-classes/${id}`),
  listIssuedTokens: (id) => api.get(`/api/asset-classes/${id}/issued-tokens`),
  create: (payload) => api.post('/api/asset-classes', payload),
  update: (id, payload) => api.put(`/api/asset-classes/${id}`, payload),
  listTokenizerCustodyVaults: (classId) => api.get(`/api/asset-classes/${classId}/custody-vault/for-tokenizers`),
  setCustodyVaultForTokenizer: (classId, tokenizerId, payload) =>
    api.put(`/api/asset-classes/${classId}/custody-vault/for-tokenizer/${tokenizerId}`, payload),
  prepareDeployCustodyVaultForTokenizer: (classId, tokenizerId, payload) =>
    api.post(`/api/asset-classes/${classId}/custody-vault/for-tokenizer/${tokenizerId}/deploy-contract/prepare`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  deployCustodyVaultForTokenizer: (classId, tokenizerId, payload) =>
    api.post(`/api/asset-classes/${classId}/custody-vault/for-tokenizer/${tokenizerId}/deploy-contract`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  listTokenizerFractionTokens: (classId) => api.get(`/api/asset-classes/${classId}/fraction-token/for-tokenizers`),
  setFractionTokenForTokenizer: (classId, tokenizerId, payload) =>
    api.put(`/api/asset-classes/${classId}/fraction-token/for-tokenizer/${tokenizerId}`, payload),
  prepareDeployFractionTokenForTokenizer: (classId, tokenizerId, payload) =>
    api.post(`/api/asset-classes/${classId}/fraction-token/for-tokenizer/${tokenizerId}/deploy-contract/prepare`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  deployFractionTokenForTokenizer: (classId, tokenizerId, payload) =>
    api.post(`/api/asset-classes/${classId}/fraction-token/for-tokenizer/${tokenizerId}/deploy-contract`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  suspend: (id) => api.post(`/api/asset-classes/${id}/suspend`),
  prepareDeployContract: (id, payload) =>
    api.post(`/api/asset-classes/${id}/deploy-contract/prepare`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  deployContract: (id, payload) =>
    api.post(`/api/asset-classes/${id}/deploy-contract`, payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  /** Admin — prepare owner-signed setMinter on a platform-shared catalog contract. */
  prepareSetContractMinter: (id, payload) =>
    api.post(`/api/asset-classes/${id}/contract/set-minter/prepare`, payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    })
};

export const assetsApi = {
  list: () => api.get('/api/assets'),
  getById: (id) => api.get(`/api/assets/${id}`),
  create: (payload) => api.post('/api/assets', payload),
  update: (id, payload) => api.put(`/api/assets/${id}`, payload),
  uploadDocument: (id, payload) =>
    api.post(`/api/assets/${id}/documents`, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  submitForLegalReview: (id) => api.post(`/api/assets/${id}/submit-for-legal-review`),
  prepareVaultLock: (id, payload) =>
    api.post(`/api/assets/${id}/vault-lock/prepare`, payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  submitVaultLock: (id, payload) =>
    api.post(`/api/assets/${id}/vault-lock`, payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  getVaultLock: (id) => api.get(`/api/assets/${id}/vault-lock`),
  prepareFractionalShares: (id, payload) =>
    api.post(`/api/assets/${id}/fractional-shares/prepare`, payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  submitFractionalShares: (id, payload) =>
    api.post(`/api/assets/${id}/fractional-shares`, payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  getFractionalShares: (id) => api.get(`/api/assets/${id}/fractional-shares`)
};

/**
 * Mirrors `AssetBulkRegistrationsController`:
 * GET list, GET by id, POST create (`CreateBulkAssetRegistrationRequest`), POST submit-for-legal-review.
 */
export const assetBulkRegistrationsApi = {
  list: () => api.get('/api/asset-bulk-registrations'),
  getById: (id) => api.get(`/api/asset-bulk-registrations/${id}`),
  create: (payload) => api.post('/api/asset-bulk-registrations', payload),
  submitForLegalReview: (bulkRegistrationId) =>
    api.post(`/api/asset-bulk-registrations/${bulkRegistrationId}/submit-for-legal-review`)
};

export const legalReviewsApi = {
  list: () => api.get('/api/legal-reviews'),
  assignableReviewers: () => api.get('/api/legal-reviews/assignable-reviewers'),
  getById: (id) => api.get(`/api/legal-reviews/${id}`),
  assign: (id, payload) => api.post(`/api/legal-reviews/${id}/assign`, payload),
  approve: (id, payload) => api.post(`/api/legal-reviews/${id}/approve`, payload),
  reject: (id, payload) => api.post(`/api/legal-reviews/${id}/reject`, payload),
  addComment: (id, payload) => api.post(`/api/legal-reviews/${id}/comments`, payload)
};

export const blockchainApi = {
  getStatus: () => api.get('/api/blockchain/status'),
  getTransaction: (transactionHash) => api.get(`/api/blockchain/transactions/${transactionHash}`),
  ownerOf: (payload) => api.post('/api/blockchain/contracts/owner-of', payload),
  tokenUri: (payload) => api.post('/api/blockchain/contracts/token-uri', payload),
  balanceOf: (payload) => api.post('/api/blockchain/contracts/balance-of', payload)
};

export const ipfsApi = {
  downloadByCid: (cid) =>
    api.get(`/api/ipfs/${encodeURIComponent(String(cid).trim())}`, {
      responseType: 'blob',
      timeout: IPFS_DOWNLOAD_TIMEOUT_MS
    }),
  listByEntity: (entityType, entityId) =>
    api.get(`/api/ipfs/entity/${entityType}/${entityId}`)
};

export const mintRequestsApi = {
  create: (payload) => api.post('/api/mint-requests', payload),
  prepare: (payload) =>
    api.post('/api/mint-requests/prepare', payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  bulkPrepareByRegistration: (payload) =>
    api.post('/api/mint-requests/bulk-prepare-by-registration', payload, {
      timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS
    }),
  bulkCreate: (payload) =>
    api.post('/api/mint-requests/bulk-create', payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  allocateErc721TokenIds: (payload) => api.post('/api/mint-requests/allocate-erc721-token-ids', payload),
  allocateErc1155TokenIds: (payload) => api.post('/api/mint-requests/allocate-erc1155-token-ids', payload),
  getByAssetId: (assetId) => api.get(`/api/mint-requests/asset/${assetId}`),
  listHistoryForAsset: (assetId) => api.get(`/api/mint-requests/asset/${assetId}/history`),
  listBulkMemberMintHistory: (bulkRegistrationId) =>
    api.get(`/api/mint-requests/bulk-registration/${bulkRegistrationId}/member-mint-history`),
  rebroadcast: (mintRequestId) =>
    api.post(`/api/mint-requests/${mintRequestId}/rebroadcast`, {}, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS })
};

/** Same operator rules as mint: owner or on-chain minters/operators for catalog contracts. */
export const burnRequestsApi = {
  prepare: (payload) =>
    api.post('/api/burn-requests/prepare', payload, { timeout: BLOCKCHAIN_RPC_API_TIMEOUT_MS }),
  create: (payload) => api.post('/api/burn-requests', payload)
};
